import React from 'react';
import {
  Theme,
  createStyles,
  makeStyles,
  Button,
  Popover,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from '@material-ui/core';
import MapSettingMenuIcon from '../Icons/MapSettingMenuIcon';
import {
  BAM_MAP_STYLES,
  BAM_MAP_STYLE_LIST,
  BAM_MAP_STYLE_DISPLAY_NAMES,
} from '../constants/MapStyles';
import { Survey } from '../../types';
import { MarketMapStatePatch } from '../../types/interactive/marketMap';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'inline-block',
      position: 'absolute',
      zIndex: 1,
      right: 11,
      bottom: 12,
    },
    settingMenu: {
      background: 'white',
      boxShadow: '0 0 0 2px rgb(0 0 0 / 10%)',
      borderRadius: 4,
      height: 30,
      width: 30,
      padding: 4,
      cursor: 'pointer',
      display: 'block',
      minWidth: 30,
      '&:hover': {
        background: '#f1f0f0',
      },
    },
    mapStyleClass: {
      width: 160,
      height: 240,
    },
    mapStyleTitle: {
      margin: '12px 16px 10px 16px',
      lineHeight: 1.25,
    },
    mapStyleList: {
      height: 200,
      paddingLeft: '16px',
      overflowY: 'auto',
      '& label': {
        marginRight: 0,
        height: 40,
        '& span': {
          fontSize: '0.8rem',
          lineHeight: 1,
        },
        '& svg': {
          fontSize: '1.2rem',
        },
      },
    },
  }));

/**
 * MapStyles Component
 *
 * Purpose: Provides a UI popover menu for users to select different map themes/styles.
 * The selected style is passed to parent component via onMapStyleChange callback.
 *
 * Key Features:
 * - Radio button selection for different map themes
 * - Controlled state synced with parent's currentStyle prop
 * - Integrates with localStorage persistence via parent component
 */
const MapStyles: React.FC<Props> = (props) => {
  const {
    onMapStyleChange,
    survey,
    currentStyle,
    controlPosition,
    popoverZIndex,
  } = props;
  const classes = useStyles();

  const controlStyle = React.useMemo<React.CSSProperties | undefined>(() => {
    if (!controlPosition) {
      return undefined;
    }

    return {
      top: controlPosition.top,
      right: controlPosition.right,
      bottom: controlPosition.bottom,
      left: controlPosition.left,
      zIndex: controlPosition.zIndex,
    };
  }, [controlPosition]);

  /**
   * Determines initial style selection with fallback priority:
   * 1. Use currentStyle if provided and valid (user's saved preference from localStorage)
   * 2. Use survey.marketMapStyle if set (survey-specific default)
   * 3. Fall back to CBRE Light theme
  *
   * Memoized to avoid recomputing on every render.
   * Only recalculates when currentStyle or survey.marketMapStyle changes.
   */
  const initialStyle = React.useMemo(() => {
    if (currentStyle && BAM_MAP_STYLE_LIST.includes(currentStyle)) {
      return currentStyle;
    }
    if (
      survey.marketMapStyle
      && BAM_MAP_STYLE_LIST.includes(survey.marketMapStyle)
    ) {
      return survey.marketMapStyle;
    }
    return BAM_MAP_STYLES.cbreLight;
  }, [currentStyle, survey.marketMapStyle]);

  /**
   * Handles style selection change.
   * Simply calls parent's onMapStyleChange callback.
   * The radio group value is controlled by currentStyle prop from parent,
   * so we don't need local state tracking.
  *
   * Memoized with useCallback to maintain stable reference and prevent unnecessary re-renders
   * of the RadioGroup component when props haven't changed.
   */
  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onMapStyleChange((event.target as HTMLInputElement).value);
  }, [onMapStyleChange]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const mapStyles = BAM_MAP_STYLES;
  const displayNames = BAM_MAP_STYLE_DISPLAY_NAMES;

  /**
   * Opens the map styles popover menu.
   * Calculates popover position based on button location.
   * Memoized with useCallback to maintain stable reference.
   */
  const toggleStylessVisible = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const open = Boolean(anchorEl);

  /**
   * Closes the map styles popover menu and resets position state.
   * Memoized with useCallback to maintain stable reference.
   */
  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);
  return (
    <section className={classes.root} style={controlStyle}>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        style={popoverZIndex ? { zIndex: popoverZIndex } : undefined}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <div className={classes.mapStyleClass}>
          <Typography className={classes.mapStyleTitle}>
            Map Style
          </Typography>
          <div className={classes.mapStyleList}>
            <RadioGroup value={currentStyle || initialStyle} onChange={handleChange}>
              {Object.keys(mapStyles).map((style) => (
                <FormControlLabel
                  key={style}
                  value={style}
                  id={`SC-SelectMapStyle-${displayNames[style]}`}
                  control={<Radio />}
                  label={displayNames[style]}
                />
              ))}
            </RadioGroup>
          </div>
        </div>
      </Popover>
      <Button className={classes.settingMenu} onClick={toggleStylessVisible}>
        <MapSettingMenuIcon height={24} width={24}></MapSettingMenuIcon>
      </Button>
    </section>
  );
};

/**
 * Props for MapStyles component
 *
 * onMapStyleChange: Callback fired when user selects a new style.
 *                   Parent uses this to save selection to localStorage and apply to map.
 * survey: Survey configuration object containing marketMapStyle property
 * currentStyle: Currently selected/active style (from localStorage)
 *              If provided, takes priority over survey.marketMapStyle
 */
type Props = {
  onMapStyleChange: (e: any) => void;
  survey: Survey | MarketMapStatePatch,
  currentStyle?: string,
  popoverZIndex?: number,
  controlPosition?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
    zIndex?: number;
  },
};

MapStyles.displayName = 'MapStyles';
export default React.memo(MapStyles);
